import { ReactNode } from 'react';
import Modal from './Modal';

/**
 * BlockModal — the reusable "this action was prevented, here's why" dialog.
 *
 * For DATA-INTEGRITY GUARDRAILS: cases where letting the action through would
 * corrupt a source of truth (e.g. demo data on a Jira/Excel ticket). The user
 * must understand WHAT was blocked and WHY — a corner toast is too easy to miss
 * for a hard constraint. (Low-stakes validation stays a toast; destructive
 * actions use the existing confirm dialogs.)
 *
 * Generic shell — branded header + amber warning glyph + an acknowledge button.
 * Callers pass the title/subtitle and the explanation as children. The first
 * consumer is AssignmentBlockedModal; new guardrails should compose this rather
 * than re-building the chrome. See memory: project-blocking-modal-pattern.
 */
interface BlockModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  /** Footer button label. Defaults to "Got it". */
  acknowledgeLabel?: string;
  /** The explanation body (paragraphs). */
  children: ReactNode;
}

export default function BlockModal({
  open,
  title,
  subtitle,
  onClose,
  acknowledgeLabel = 'Got it',
  children,
}: BlockModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      branded
      title={title}
      subtitle={subtitle}
      footer={
        <button
          onClick={onClose}
          className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-semibold hover:bg-brand-primary-dark transition-colors"
        >
          {acknowledgeLabel}
        </button>
      }
    >
      <div className="flex gap-3">
        <div className="shrink-0 mt-0.5 flex items-center justify-center w-9 h-9 rounded-full bg-amber-100 text-amber-600 text-lg">
          ⚠
        </div>
        <div className="text-sm text-ink-muted space-y-3">{children}</div>
      </div>
    </Modal>
  );
}
